import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Button, Table, Spin, Tag, Modal, QRCode } from 'antd';
import { antdMessage } from '../../utils/antdGlobal';
import { FileTextOutlined, DownloadOutlined } from '@ant-design/icons';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { queryGrades, generateReport } from '../../services/query';
import { getPublicConfigValue } from '../../services/config';
import { GradeQueryResult, Grade } from '../../types';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import './style.css';

/**
 * 成绩查询页（公众入口）
 * 设计基调：学府印信 —— 学术公文 / 档案处美学
 * 学生与家长在此查询在档成绩，并可生成带公章与二维码的正式验证报告
 */
const GradeQuery: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GradeQueryResult | null>(null);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [validityDays, setValidityDays] = useState(180);
  const [systemTitle, setSystemTitle] = useState('成绩查询验证系统');
  const { isMobile } = useBreakpoint();
  const reportRef = useRef<HTMLDivElement>(null);

  // 获取系统标题配置
  useEffect(() => {
    const fetchSystemTitle = async () => {
      try {
        const result = await getPublicConfigValue('system_title');
        if (result.value) {
          setSystemTitle(result.value);
          document.title = result.value;
        }
      } catch (error) {
        // 配置不存在时使用默认值
      }
    };
    fetchSystemTitle();
  }, []);

  /**
   * 提交查询：至少提供学号 / 身份证号 / 姓名中的两项
   */
  const handleSubmit = async (values: any) => {
    const providedFields = [values.studentNo, values.idNumber, values.name].filter(Boolean);
    if (providedFields.length < 2) {
      antdMessage.error('请至少提供学号、身份证号、姓名中的两项信息');
      return;
    }

    setLoading(true);
    try {
      const res = await queryGrades(values);
      setResult(res);
      antdMessage.success('查询成功');
    } catch (error) {
      antdMessage.error('查询失败，请检查输入信息');
    } finally {
      setLoading(false);
    }
  };

  /**
   * 生成带二维码与公章的验证报告
   */
  const handleGenerateReport = async () => {
    if (!result?.studentInfo.id) return;

    try {
      const res = await generateReport(result.studentInfo.id, validityDays);
      setReportData(res);
      setReportModalVisible(true);
      antdMessage.success('验证报告生成成功');
    } catch (error) {
      antdMessage.error('报告生成失败');
    }
  };

  /**
   * 根据当前访问的 origin 动态构建完整验证链接
   * 兼容：旧数据（绝对URL）和新数据（相对路径）
   */
  const getFullVerifyLink = (verifyLink: string): string => {
    if (!verifyLink) return '';
    if (verifyLink.startsWith('http://') || verifyLink.startsWith('https://')) {
      try {
        return `${getApiOrigin()}${new URL(verifyLink).pathname}`;
      } catch {
        return verifyLink;
      }
    }
    return `${getApiOrigin()}${verifyLink}`;
  };

  // 获取 API 基础域名（开发环境用后端端口，生产环境用当前域名）
  const getApiOrigin = (): string => {
    const apiUrl = process.env.REACT_APP_API_URL || '/api';
    if (apiUrl.startsWith('http://') || apiUrl.startsWith('https://')) {
      return apiUrl.replace('/api', '');
    }
    return window.location.origin;
  };

  /**
   * 将报告区域渲染为 PDF 并下载
   */
  const handleDownloadPDF = async () => {
    if (!reportRef.current) {
      antdMessage.error('报告内容未加载');
      return;
    }

    try {
      antdMessage.loading('正在生成PDF...', 0);

      // 等待二维码渲染完成
      await new Promise(resolve => setTimeout(resolve, 500));

      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);

      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);

      const fileName = `成绩验证报告_${result?.studentInfo.name || '学生'}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);

      antdMessage.destroy();
      antdMessage.success('PDF下载成功');
    } catch (error) {
      antdMessage.destroy();
      console.error('PDF生成失败:', error);
      antdMessage.error('PDF生成失败，请重试');
    }
  };

  // 桌面端成绩表格列定义
  const columns = [
    {
      title: '课程名称',
      dataIndex: 'courseName',
      key: 'courseName',
    },
    {
      title: '课程代码',
      dataIndex: 'courseCode',
      key: 'courseCode',
      render: (code: string) => <span className="qp-mono">{code}</span>,
    },
    {
      title: '课程类别',
      dataIndex: 'courseCategory',
      key: 'courseCategory',
      render: (category: string) => <Tag className="qp-tag">{category}</Tag>,
    },
    {
      title: '学分',
      dataIndex: 'credits',
      key: 'credits',
      render: (v: number) => <span className="qp-mono">{v}</span>,
    },
    {
      title: '学时',
      dataIndex: 'hours',
      key: 'hours',
      render: (v: number) => <span className="qp-mono">{v}</span>,
    },
    {
      title: '成绩',
      key: 'grade',
      render: (_: any, record: Grade) => (
        <span className="qp-grade-cell">
          <strong className="qp-grade-value">{record.gradeValue}</strong>
          <Tag className={`qp-tag ${record.gradeType === 'percentage' ? 'qp-tag-vermilion' : 'qp-tag-gilt'}`}>
            {record.gradeType === 'percentage' ? '百分制' : '等级制'}
          </Tag>
        </span>
      ),
    },
  ];

  return (
    <div className="query-page">
      {/* ===== 公文报头：机构名牌 + 双线规 ===== */}
      <header className="qp-masthead">
        <div className="qp-masthead-inner">
          <div className="qp-brand">
            <span className="qp-seal" aria-hidden="true">验</span>
            <div className="qp-brand-text">
              <h1 className="qp-title">{systemTitle}</h1>
              <p className="qp-subtitle">ACADEMIC RECORD VERIFICATION SERVICE</p>
            </div>
          </div>
          <div className="qp-masthead-meta">
            <span className="qp-meta-badge">官方验证通道</span>
            <span className="qp-meta-no">NO.{new Date().getFullYear()}-REGISTRY</span>
          </div>
        </div>
      </header>

      <main className="qp-main">
        {/* 竖排装饰文字（宽屏显示） */}
        <aside className="qp-vertical-note" aria-hidden="true">
          <span>成绩在档</span>
          <i />
          <span>扫码可验</span>
        </aside>

        {/* ===== 查询公文纸 ===== */}
        <section className="qp-document">
          <span className="qp-corner qp-corner-tl" />
          <span className="qp-corner qp-corner-tr" />
          <span className="qp-corner qp-corner-bl" />
          <span className="qp-corner qp-corner-br" />

          <header className="qp-doc-header">
            <div className="qp-doc-heading">
              <h2 className="qp-doc-title">成绩查询</h2>
              <p className="qp-doc-sub">GRADE INQUIRY · 在档成绩检索</p>
            </div>
            <div className="qp-doc-serial">
              <span>受理窗口</span>
              <strong>QUERY—01</strong>
            </div>
          </header>

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            autoComplete="off"
            className="qp-form"
          >
            <div className="qp-form-grid">
              <Form.Item name="studentNo" label="学　号">
                <Input placeholder="请输入学号" size="large" />
              </Form.Item>

              <Form.Item name="name" label="姓　名">
                <Input placeholder="请输入姓名" size="large" />
              </Form.Item>

              <Form.Item name="idNumber" label="身份证号">
                <Input placeholder="请输入身份证号" size="large" />
              </Form.Item>
            </div>

            <p className="qp-form-notice">
              <span className="qp-notice-mark">※</span>
              请至少填写以上两项信息，核验通过后可在线生成正式验证报告
            </p>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                block
                size="large"
                className="qp-submit"
              >
                查　询
              </Button>
            </Form.Item>
          </Form>

          {loading && (
            <div className="qp-loading">
              <Spin size="large" />
              <p>正在检索档案…</p>
            </div>
          )}

          {/* ===== 查询结果：档案核验 ===== */}
          {result && !loading && (
            <section className="qp-result">
              <div className="qp-result-divider">
                <span className="qp-result-divider-text">核 验 通 过</span>
              </div>

              {/* 学生档案信息 */}
              <div className="qp-dossier">
                <div className="qp-dossier-head">
                  <h3>学生档案</h3>
                  <span className="qp-mono">STUDENT RECORD</span>
                </div>
                <dl className="qp-dossier-grid">
                  <div className="qp-dossier-item">
                    <dt>姓　　名</dt>
                    <dd>{result.studentInfo.name}</dd>
                  </div>
                  <div className="qp-dossier-item">
                    <dt>专　　业</dt>
                    <dd>{result.studentInfo.major}</dd>
                  </div>
                  <div className="qp-dossier-item">
                    <dt>学　　号</dt>
                    <dd className="qp-mono">{result.studentInfo.studentNo}</dd>
                  </div>
                  <div className="qp-dossier-item">
                    <dt>校　　区</dt>
                    <dd>{result.studentInfo.campusName}</dd>
                  </div>
                  <div className="qp-dossier-item">
                    <dt>在档成绩</dt>
                    <dd className="qp-mono">{result.totalGrades} 条</dd>
                  </div>
                </dl>
              </div>

              {/* 成绩明细（按学期） */}
              <div className="qp-transcript">
                <div className="qp-dossier-head">
                  <h3>成绩明细</h3>
                  <span className="qp-mono">TRANSCRIPT</span>
                </div>
                {Object.entries(result.gradesBySemester).map(([semester, grades]) => (
                  <div key={semester} className="qp-semester">
                    <h4 className="qp-semester-title">
                      <span className="qp-semester-rule" />
                      {semester}
                      <span className="qp-semester-rule" />
                    </h4>
                    {isMobile ? (
                      /* 移动端：档案卡片列表 */
                      <div>
                        {(grades as Grade[]).map((item: Grade, itemIndex: number) => (
                          <div key={`${item.courseCode}-${itemIndex}`} style={{ padding: '6px 0' }}>
                            <div className="qp-grade-card">
                              <div className="qp-grade-card-head">
                                <span className="qp-grade-card-name">{item.courseName}</span>
                                <strong className="qp-grade-value">{item.gradeValue}</strong>
                              </div>
                              <div className="qp-grade-card-meta">
                                <Tag className="qp-tag">{item.courseCategory}</Tag>
                                <span className="qp-mono">{item.courseCode}</span>
                                <span className="qp-grade-card-credit">{item.credits} 学分 · {item.hours} 学时</span>
                                <Tag className={`qp-tag ${item.gradeType === 'percentage' ? 'qp-tag-vermilion' : 'qp-tag-gilt'}`}>
                                  {item.gradeType === 'percentage' ? '百分制' : '等级制'}
                                </Tag>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* 桌面端：公文表格 */
                      <Table
                        className="qp-table"
                        columns={columns}
                        dataSource={grades as Grade[]}
                        rowKey={(record, index) => `${semester}-${index}`}
                        pagination={false}
                        size="middle"
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* 生成验证报告 */}
              <div className="qp-report-cta">
                <Button
                  type="primary"
                  icon={<FileTextOutlined />}
                  onClick={handleGenerateReport}
                  size="large"
                  block={isMobile}
                  className="qp-report-btn"
                >
                  生成验证报告
                </Button>
                <p>报告附电子公章与验证二维码，供国内外院校在线核验</p>
              </div>
            </section>
          )}
        </section>

        {/* ===== 页脚 ===== */}
        <footer className="qp-footer">
          <span className="qp-footer-rule" />
          <p>© {new Date().getFullYear()} {systemTitle} · 验证报告仅供院校及用人单位核验使用</p>
        </footer>
      </main>

      {/* ===== 验证报告弹窗 ===== */}
      <Modal
        title={null}
        open={reportModalVisible}
        onCancel={() => setReportModalVisible(false)}
        footer={[
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownloadPDF}
            className="report-download-btn"
          >
            下载PDF
          </Button>,
          <Button key="close" onClick={() => setReportModalVisible(false)} className="report-close-btn">
            关闭
          </Button>,
        ]}
        width={isMobile ? '100%' : 800}
        className="report-modal"
        style={isMobile ? { top: 0, margin: 0, padding: 0, maxWidth: '100vw' } : {}}
      >
        {reportData && result && (
          <div className="verification-report" ref={reportRef}>
            {/* 报告边框装饰 */}
            <div className="report-border">
              {/* 报告头部 */}
              <div className="report-header">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 8, flexWrap: 'wrap' }}>
                  {reportData.badgeImagePath && (
                    <img src={reportData.badgeImagePath} alt="校徽" style={{ height: 60, objectFit: 'contain' }} crossOrigin="anonymous" />
                  )}
                  {reportData.schoolNameImagePath ? (
                    <img src={reportData.schoolNameImagePath} alt="校名" style={{ height: 40, objectFit: 'contain' }} crossOrigin="anonymous" />
                  ) : (
                    <h2 className="report-title" style={{ margin: 0 }}>{result.studentInfo.campusName || '学校'}学生</h2>
                  )}
                </div>
                <h2 className="report-title">成绩验证报告</h2>
              </div>

              {/* 报告信息 */}
              <div className="report-meta">
                <div className="report-meta-row">
                  <span className="report-meta-label">报告编号：</span>
                  <span className="report-meta-value">{reportData.reportNo}</span>
                </div>
                <div className="report-meta-row">
                  <span className="report-meta-label">打印日期：</span>
                  <span className="report-meta-value">{new Date().toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\//g, '-')}</span>
                </div>
                <div className="report-meta-row">
                  <span className="report-meta-label">学生姓名：</span>
                  <span className="report-meta-value">{result.studentInfo.name}</span>
                </div>
                <div className="report-meta-row">
                  <span className="report-meta-label">专业：</span>
                  <span className="report-meta-value">{result.studentInfo.major}</span>
                </div>
                <div className="report-meta-row">
                  <span className="report-meta-label">学号：</span>
                  <span className="report-meta-value">{result.studentInfo.studentNo}</span>
                </div>
              </div>

              {/* 成绩表格 */}
              <div className="report-grades">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>课程名称</th>
                      <th>成绩</th>
                      <th>学分</th>
                      <th>学时</th>
                      <th>课程类别</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(result.gradesBySemester).map(([semester, grades]) => (
                      <React.Fragment key={semester}>
                        <tr className="semester-row">
                          <td colSpan={5}>{semester}：</td>
                        </tr>
                        {(grades as Grade[]).map((item, index) => (
                          <tr key={index}>
                            <td>{item.courseName}</td>
                            <td className="grade-value">{item.gradeValue}</td>
                            <td>{item.credits}</td>
                            <td>{item.hours}</td>
                            <td>{item.courseCategory}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 底部声明 */}
              <div className="report-footer">
                <p className="report-declaration">以上成绩单内容经核实无误。</p>
                <p className="report-note">（以下无正文）</p>
              </div>

              {/* 联系方式与签名 */}
              {(reportData.contactEmail || reportData.contactAddress || reportData.contactPhone) && (
                <div className="report-contact" style={{ padding: '0 40px', marginTop: 16, fontSize: 13, color: '#555' }}>
                  {reportData.contactEmail && <div style={{ marginBottom: 4 }}>邮箱：{reportData.contactEmail}</div>}
                  {reportData.contactAddress && <div style={{ marginBottom: 4 }}>地址：{reportData.contactAddress}</div>}
                  {reportData.contactPhone && <div style={{ marginBottom: 4 }}>电话：{reportData.contactPhone}</div>}
                  {reportData.signatureImagePath && (
                    <div style={{ textAlign: 'right', marginTop: 8 }}>
                      <img src={reportData.signatureImagePath} alt="负责人签字" style={{ height: 45, objectFit: 'contain' }} crossOrigin="anonymous" />
                      <div style={{ fontSize: 12, color: '#999' }}>负责人签字</div>
                    </div>
                  )}
                </div>
              )}

              {/* 底部信息 */}
              <div className="report-bottom">
                <div className="report-qr-section">
                  <QRCode value={getFullVerifyLink(reportData.verifyLink)} size={100} />
                  <div className="report-qr-info">
                    <p>报告在线验证网址：</p>
                    <p className="report-url">{getFullVerifyLink(reportData.verifyLink)}</p>
                  </div>
                </div>
                <div className="report-stamp-section">
                  {/* 电子公章：优先显示配置的公章图片，否则显示圆形文字章 */}
                  <div className="report-stamp">
                    {reportData.sealImageUrl ? (
                      <img
                        src={reportData.sealImageUrl}
                        alt="电子公章"
                        className="stamp-image"
                        crossOrigin="anonymous"
                      />
                    ) : (
                      <div className="stamp-circle">
                        <div className="stamp-text-top">{result.studentInfo.campusName || '学校'}</div>
                        <div className="stamp-star">★</div>
                        <div className="stamp-text-bottom">{reportData.sealText || '成绩专用章'}</div>
                      </div>
                    )}
                  </div>
                  <div className="report-validity">
                    <p>有效期至：{new Date(reportData.expiredAt).toLocaleDateString('zh-CN')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default GradeQuery;
